import { useMemo } from "react";
import { toNumber } from "@/shared/utils";
import { RENTAL_PAYMENT_STATUS_ORDER } from "@/features/accounting/shared";
import type { RentalPaymentListItem } from "../types";

export type RentalPaymentSortKey =
  | "status"
  | "date"
  | "period"
  | "amount"
  | "vendor"
  | "type"
  | "avr_date"
  | "sf_number"
  | "sf_date";

export interface RentalPaymentSortConfig {
  key: RentalPaymentSortKey;
  direction: "asc" | "desc";
}

export function useRentalPaymentsSort(
  payments: RentalPaymentListItem[],
  sortConfig: RentalPaymentSortConfig | null,
): RentalPaymentListItem[] {
  return useMemo(() => {
    if (!sortConfig) {
      return payments;
    }

    const { key, direction } = sortConfig;

    return [...payments].sort((a, b) => {
      let cmp = 0;

      if (key === "status") {
        const aStatus = RENTAL_PAYMENT_STATUS_ORDER[a.status] ?? -1;
        const bStatus = RENTAL_PAYMENT_STATUS_ORDER[b.status] ?? -1;
        cmp = aStatus - bStatus;
      } else if (key === "date") {
        const aDate = new Date(a.avr_date).getTime();
        const bDate = new Date(b.avr_date).getTime();
        cmp = aDate - bDate;
      } else if (key === "period") {
        const aDate = new Date(a.period_start_date).getTime();
        const bDate = new Date(b.period_start_date).getTime();
        cmp = aDate - bDate;
      } else if (key === "amount") {
        cmp = toNumber(a.amount_total) - toNumber(b.amount_total);
      } else if (key === "vendor") {
        cmp = a.vendor_name.localeCompare(b.vendor_name);
      } else if (key === "type") {
        cmp = a.rental_type.localeCompare(b.rental_type);
      } else if (key === "avr_date") {
        const aDate = new Date(a.avr_date).getTime();
        const bDate = new Date(b.avr_date).getTime();
        cmp = aDate - bDate;
      } else if (key === "sf_number") {
        const aNumber = a.sf_number ?? "";
        const bNumber = b.sf_number ?? "";
        cmp = aNumber.localeCompare(bNumber, undefined, { numeric: true, sensitivity: "base" });
      } else if (key === "sf_date") {
        const aMissing = !a.sf_date;
        const bMissing = !b.sf_date;
        if (aMissing && bMissing) {
          cmp = 0;
        } else if (aMissing) {
          cmp = 1;
        } else if (bMissing) {
          cmp = -1;
        } else {
          const aDate = new Date(a.sf_date!).getTime();
          const bDate = new Date(b.sf_date!).getTime();
          cmp = aDate - bDate;
        }
      }

      return direction === "asc" ? cmp : -cmp;
    });
  }, [payments, sortConfig]);
}

export function toggleRentalPaymentSort(
  currentSort: RentalPaymentSortConfig | null,
  newKey: RentalPaymentSortKey,
): RentalPaymentSortConfig {
  if (currentSort?.key === newKey) {
    return { key: newKey, direction: currentSort.direction === "asc" ? "desc" : "asc" };
  }
  return { key: newKey, direction: "asc" };
}

